import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import ChristmasPartyContactForm from "./ChristmasPartyContactForm";

// Swap these for cropped versions of your flyer artwork —
// one for the tartan section, one for the botanical/green section
import threeCoursePoster from "../assets/images/Events/threeCourse.png";
import twoCoursePoster from "../assets/images/Events/poshBuffet.png";

const stripePromise = loadStripe("pk_live_51TI3lpDNGgQknfKITg09mV6QoVFT5WiLso1PadWoOQQSnpouYygHaHBJLZGJ7bStYm2ZkXmBlw2HFSjbFXbdgtyp00RzZHAEdj");

// API endpoints for each date
const API_ENDPOINTS = {
  "Friday 11th December": "https://thefoxapi.azurewebsites.net/api/checkout/dec11",
  "Saturday 12th December": "https://thefoxapi.azurewebsites.net/api/checkout/dec12",
  "Friday 18th December": "https://thefoxapi.azurewebsites.net/api/checkout/dec18",
  "Saturday 19th December": "https://thefoxapi.azurewebsites.net/api/checkout/dec19"
};

// Mapping between display name and stock RowKey
const STOCK_KEYS = {
  "Friday 11th December": "dec11",
  "Saturday 12th December": "dec12",
  "Friday 18th December": "dec18",
  "Saturday 19th December": "dec19"
};

// Two packages, two colour stories — pulled straight from the flyer
const PACKAGES = {
  threeCourse: {
    key: "threeCourse",
    label: "Three Course Seated Feast",
    dateRange: "11th & 12th December",
    dates: ["Friday 11th December", "Saturday 12th December"],
    fullPrice: 50,
    poster: threeCoursePoster,
    description:
      "A seated three course feast paired with a glass of fizz on arrival. Dress up, settle in, and let the night unfold.",
    features: [
      "Glass of fizz on arrival at 6:30pm",
      "Three course seated feast from 7 till 9pm",
      "Festive tartan-dressed dining room",
      "Dance the night away after dinner till 12:30am"
    ],
    theme: {
      gradient: "from-[#3a0d14] via-[#2d4a3e] to-[#0d1a12]",
      panelBorder: "border-red-400/30",
      panelBg: "bg-red-950/20",
      accentText: "text-red-300",
      headingText: "text-red-200",
      pillActive: "bg-red-700 text-white border-red-700",
      pillInactive: "text-red-100 border-red-300/40 hover:bg-red-900/40",
      button: "bg-red-600 hover:bg-red-700",
      badge: "bg-red-500/20 text-red-200 border-red-400/40"
    }
  },
  twoCourse: {
    key: "twoCourse",
    label: "Two Course Posh Buffet & Dessert",
    dateRange: "18th & 19th December",
    dates: ["Friday 18th December", "Saturday 19th December"],
    fullPrice: 50,
    poster: twoCoursePoster,
    description:
      "A relaxed but seriously posh buffet, followed by a luxury dessert spread. Live band, DJ, and holly-dressed decor all night.",
    features: [
      "Glass of fizz on arrival at 6:30pm",
      "Two course posh buffet from 7 till 9pm",
      "Luxury dessert spread",
      "Live band & DJ until 12:30am"
    ],
    theme: {
      gradient: "from-[#0f2416] via-[#16321f] to-[#0a1a10]",
      panelBorder: "border-amber-400/30",
      panelBg: "bg-emerald-950/20",
      accentText: "text-amber-300",
      headingText: "text-amber-200",
      pillActive: "bg-emerald-700 text-white border-emerald-700",
      pillInactive: "text-emerald-100 border-amber-300/30 hover:bg-emerald-900/40",
      button: "bg-emerald-600 hover:bg-emerald-700",
      badge: "bg-amber-500/20 text-amber-200 border-amber-400/40"
    }
  }
};

const DEPOSIT = 25.0;

const ProductDisplay = () => {
  const [packageKey, setPackageKey] = useState("threeCourse");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [stock, setStock] = useState({});
  const [stockLoading, setStockLoading] = useState(true);

  const pkg = PACKAGES[packageKey];
  const theme = pkg.theme;
  const total = (quantity * DEPOSIT).toFixed(2);

  // Fetch stock from Azure Function
  useEffect(() => {
    const fetchStock = async () => {
      try {
        const res = await fetch("https://thefoxapi.azurewebsites.net/api/get-stock?");
        const data = await res.json();
        setStock(data);
      } catch (err) {
        console.error("Error fetching stock:", err);
      } finally {
        setStockLoading(false);
      }
    };
    fetchStock();
  }, []);

  // Reset date selection when switching packages
  const handlePackageSwitch = (key) => {
    setPackageKey(key);
    setSelectedDate("");
    setQuantity(1);
  };

  const handleClick = async () => {
    if (!selectedDate) {
      alert("Please select a date first.");
      return;
    }

    if (!quantity || quantity < 1) {
      alert("Please enter a valid quantity (1 or more).");
      return;
    }

    const available = stock[STOCK_KEYS[selectedDate]];
    if (available !== undefined && quantity > available) {
      alert(`Only ${available} tickets left for ${selectedDate}`);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(API_ENDPOINTS[selectedDate], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity })
      });

      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Server error: ${res.status} - ${error}`);
      }

      const data = await res.json();
      const stripe = await stripePromise;
      stripe.redirectToCheckout({ sessionId: data.id });
    } catch (err) {
      console.error("Stripe checkout error:", err);
      alert("Checkout failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const snowflakes = Array.from({ length: 35 });

  const needsContactForm = quantity < 6 || quantity > 10;

  return (
    <section
      id="partyBookings"
      className="relative min-h-screen overflow-hidden py-24 px-4 font-sans"
    >
      {/* Crossfading themed background */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={packageKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className={`absolute inset-0 bg-gradient-to-b ${theme.gradient}`}
          />
        </AnimatePresence>
      </div>

      {/* Snow texture */}
      <div
        className="absolute inset-0 opacity-20 bg-[radial-gradient(circle,#fff_1px,transparent_1px)] bg-[length:25px_25px]"
      />

      {/* Animated snowfall */}
      {snowflakes.map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-white opacity-70 pointer-events-none"
          style={{
            left: `${Math.random() * 100}%`,
            top: `-${Math.random() * 20}%`,
            fontSize: `${Math.random() * 12 + 8}px`
          }}
          animate={{ y: ["0vh", "120vh"], rotate: [0, 360] }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            delay: Math.random() * 5
          }}
        >
          ❄
        </motion.div>
      ))}

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: -60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.3 }}
        transition={{ duration: 1 }}
        className="relative z-10 text-center mb-14"
      >
        <p className="tracking-[12px] uppercase text-red-300 font-semibold">
          The Fox Inn Presents
        </p>
        <h2 className="mt-5 text-5xl md:text-7xl font-serif text-white drop-shadow-xl">
          Christmas Party Nights
        </h2>
        <p className="mt-4 text-xl uppercase tracking-[8px] text-white/80">
          Book Your Table
        </p>
      </motion.div>

      {/* Package toggle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.3 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 flex justify-center gap-3 mb-16 flex-wrap"
      >
        {Object.values(PACKAGES).map((p) => (
          <motion.button
            key={p.key}
            onClick={() => handlePackageSwitch(p.key)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`px-6 py-3 rounded-full border-2 font-medium tracking-wide transition-colors duration-300 backdrop-blur-md ${
              packageKey === p.key ? p.theme.pillActive : `bg-white/5 ${p.theme.pillInactive}`
            }`}
          >
            {p.label} · {p.dateRange}
          </motion.button>
        ))}
      </motion.div>

      {/* Main content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={packageKey}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 px-2 items-center"
        >
          {/* Poster */}
          <motion.div
            whileHover={{ rotateY: 8, scale: 1.02 }}
            className="perspective-1000"
          >
            <img
              src={pkg.poster}
              alt={pkg.label}
              className="rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,.5)] border border-white/20 w-full"
            />
          </motion.div>

          {/* Details + booking card */}
          <div
            className={`backdrop-blur-xl bg-white/10 border ${theme.panelBorder} rounded-3xl p-8 shadow-2xl`}
          >
            <span
              className={`inline-block text-xs uppercase tracking-[3px] px-3 py-1 rounded-full border ${theme.badge} mb-4`}
            >
              {pkg.dateRange}
            </span>

            <h3 className={`text-4xl md:text-5xl font-serif ${theme.headingText}`}>
              {pkg.label}
            </h3>

            <p className="mt-5 text-white/90 text-base leading-7">
              {pkg.description}
            </p>

            <div className="mt-7 space-y-3 text-white/90">
              {pkg.features.map((feature, i) => (
                <motion.p
                  key={feature}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ amount: 0.5 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="border-b border-white/10 pb-3 last:border-none"
                >
                  {feature}
                </motion.p>
              ))}
            </div>

            <p className={`mt-7 text-center text-3xl font-bold ${theme.accentText}`}>
              £{pkg.fullPrice} Per Person
            </p>
            <p className="text-center text-white/60 text-sm mb-6">
              Secure your table with a £{DEPOSIT.toFixed(0)}pp deposit today.
              Deposit is non-refundable — final balance and food orders (3-course only) is requested by email
              one month before your party date.
            </p>

            {/* Date selection */}
            <div
              className={`${theme.panelBg} border ${theme.panelBorder} rounded-xl p-5`}
            >
              <p className="text-white font-semibold mb-3">Select a Date:</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {pkg.dates.map((date) => (
                  <motion.button
                    key={date}
                    onClick={() => setSelectedDate(date)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={`px-4 py-2 rounded-full font-medium border-2 transition-all duration-300 ${
                      selectedDate === date
                        ? theme.pillActive
                        : `bg-white/5 ${theme.pillInactive}`
                    }`}
                  >
                    {date}
                  </motion.button>
                ))}
              </div>

              <AnimatePresence>
                {selectedDate && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4 }}
                    className="overflow-hidden"
                  >
                    {!stockLoading && stock[STOCK_KEYS[selectedDate]] !== undefined && (
                      <p className="text-sm text-red-300 mb-2">
                        Only {stock[STOCK_KEYS[selectedDate]]} tickets left!
                      </p>
                    )}

                    <label className="block text-white/90 font-semibold mb-2">
                      Quantity:
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value))}
                      className="w-full mb-4 px-4 py-2 rounded-lg bg-white/90 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />

                    <p className="text-white/90 font-medium mb-4">
                      Deposit total:{" "}
                      <span className="font-bold">£{total}</span>
                    </p>

                    {needsContactForm ? (
                      <ChristmasPartyContactForm
                        selectedDate={selectedDate}
                        quantity={quantity}
                      />
                    ) : (
                      <motion.button
                        onClick={handleClick}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        disabled={
                          loading ||
                          stockLoading ||
                          (stock[STOCK_KEYS[selectedDate]] !== undefined &&
                            quantity > stock[STOCK_KEYS[selectedDate]])
                        }
                        className={`${theme.button} text-white font-bold py-2 px-6 rounded-full shadow-md transition-all duration-300 disabled:opacity-50`}
                      >
                        {loading ? "Processing..." : "🎄 Checkout"}
                      </motion.button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

const Message = ({ message }) => (
  <section className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#183758] to-[#071522] text-center p-6">
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="text-lg font-medium text-white bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-xl shadow-md max-w-md mb-6"
    >
      {message}
    </motion.p>
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => (window.location.href = "/christmas")}
      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-6 rounded-full shadow-md transition-all"
    >
      Place Another Order
    </motion.button>
  </section>
);

export default function App() {
  const [message, setMessage] = useState("");

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      setMessage("🎉 Order placed! You will receive an email confirmation.");
    }

    if (query.get("canceled")) {
      setMessage("❄️ Order canceled — feel free to continue browsing and checkout when you're ready.");
    }
  }, []);

  return message ? <Message message={message} /> : <ProductDisplay />;
}